import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getPublicProfile, getPublicGarageVehicles, getPublicVehicleDetails } from '../../lib/supabase'
import { Car, ArrowLeft, Calendar, MapPin, Gauge, AlertCircle, X, Award, Camera, Shield, Users, ArrowRight } from 'lucide-react'
import { format } from 'date-fns'

// Provenance score tier badge component
const TierBadge = ({ tier }) => {
  const tierConfig = {
    'Best in Show': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Best in Show' },
    'Concours': { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Concours' },
    'Show Ready': { bg: 'bg-green-100', text: 'text-green-800', label: 'Show Ready' },
    'Daily Driver': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Daily Driver' },
    'Barn Find': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Barn Find' },
  }

  const config = tierConfig[tier] || tierConfig['Barn Find']

  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Award size={12} />
      {config.label}
    </span>
  )
}

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    ready: { bg: 'bg-rdc-forest/20', text: 'text-rdc-forest', label: 'Ready' },
    with_owner: { bg: 'bg-rdc-forest/20', text: 'text-rdc-forest', label: 'With Owner' },
    in_service: { bg: 'bg-rdc-tan/20', text: 'text-rdc-tan', label: 'In Service' },
    stored: { bg: 'bg-rdc-olive/20', text: 'text-rdc-olive', label: 'Stored' },
    in_transit: { bg: 'bg-rdc-burgundy/20', text: 'text-rdc-burgundy', label: 'In Transit' },
  }

  const config = statusConfig[status] || statusConfig.stored

  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  )
}

// Determine documentation tier based on photos and specs
const getDocumentationTier = (vehicle) => {
  let score = 0
  if (vehicle.primary_image_url) score += 1
  if (vehicle.images && vehicle.images.length >= 3) score += 1
  if (vehicle.vin) score += 1
  if (vehicle.mileage !== null) score += 1
  if (vehicle.description) score += 1

  if (score >= 5) return 'Best in Show'
  if (score >= 4) return 'Concours'
  if (score >= 3) return 'Show Ready'
  if (score >= 2) return 'Daily Driver'
  return 'Barn Find'
}

// Enhanced vehicle card for grid
const VehicleCard = ({ vehicle, onClick }) => {
  const tier = getDocumentationTier(vehicle)

  return (
    <button
      onClick={onClick}
      className="card overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group text-left w-full h-full flex flex-col"
    >
      <div className="h-48 bg-rdc-cream overflow-hidden relative flex-shrink-0">
        {vehicle.primary_image_url ? (
          <img
            src={vehicle.primary_image_url}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-rdc-cream to-rdc-warm-gray">
            <Car size={48} className="text-rdc-warm-gray opacity-40" />
          </div>
        )}
        <div className="absolute top-3 right-3 flex flex-col gap-2 items-end">
          <TierBadge tier={tier} />
        </div>
      </div>
      <div className="p-4 flex-grow flex flex-col justify-between">
        <div>
          <div className="text-xs text-rdc-taupe mb-1 font-medium">{vehicle.year}</div>
          <h3 className="font-display text-lg font-semibold text-black mb-2 leading-tight">
            {vehicle.make} {vehicle.model}
          </h3>
          {vehicle.color && (
            <div className="text-sm text-rdc-dark-gray mb-3">{vehicle.color}</div>
          )}
        </div>
        <div className="flex items-center gap-1 text-rdc-taupe text-xs">
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          <span>View details</span>
        </div>
      </div>
    </button>
  )
}

// Enhanced vehicle detail modal
const VehicleDetailModal = ({ vehicle, onClose }) => {
  if (!vehicle) return null

  const tier = getDocumentationTier(vehicle)

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header with close button */}
        <div className="sticky top-0 bg-white border-b border-rdc-cream p-6 flex justify-between items-start z-10">
          <div>
            <h2 className="text-2xl font-display font-semibold text-black">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h2>
            <div className="flex items-center gap-3 mt-2">
              <p className="text-rdc-taupe text-sm">{vehicle.color}</p>
              <TierBadge tier={tier} />
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-rdc-cream rounded-lg transition-colors flex-shrink-0"
          >
            <X size={24} className="text-rdc-dark-gray" />
          </button>
        </div>

        {/* Images gallery */}
        {vehicle.images && vehicle.images.length > 0 && (
          <div className="px-6 pt-6 pb-4">
            <h3 className="section-title mb-4 flex items-center gap-2">
              <Camera size={16} />
              Gallery
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {vehicle.images.map((img) => (
                <img
                  key={img.id}
                  src={img.image_url}
                  alt="Vehicle"
                  className="rounded-lg w-full h-48 object-cover hover:shadow-lg transition-shadow"
                />
              ))}
            </div>
          </div>
        )}

        {/* Details */}
        <div className="px-6 py-6 space-y-6">
          {/* Status and specs */}
          <div>
            <h3 className="section-title mb-3">Specifications</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-rdc-cream rounded-lg p-4">
                <div className="text-xs text-rdc-taupe mb-1">Status</div>
                <div className="mt-2">
                  <StatusBadge status={vehicle.status} />
                </div>
              </div>
              {vehicle.vin && (
                <div className="bg-rdc-cream rounded-lg p-4">
                  <div className="text-xs text-rdc-taupe mb-1">VIN</div>
                  <div className="text-sm font-medium text-black break-all">{vehicle.vin}</div>
                </div>
              )}
              {vehicle.mileage !== null && (
                <div className="bg-rdc-cream rounded-lg p-4">
                  <div className="text-xs text-rdc-taupe mb-1 flex items-center gap-1">
                    <Gauge size={14} /> Mileage
                  </div>
                  <div className="text-sm font-medium text-black">
                    {vehicle.mileage.toLocaleString()} miles
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {vehicle.description && (
            <div>
              <h3 className="section-title mb-3">About This Vehicle</h3>
              <p className="text-rdc-dark-gray leading-relaxed">{vehicle.description}</p>
            </div>
          )}

          {/* Privacy notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <Shield size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-900">
                This shared garage shows photos, specifications, and documentation status. Valuations, financial records, and personal details are never shared.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function PublicGarage() {
  const { userId } = useParams()
  const [profile, setProfile] = useState(null)
  const [vehicles, setVehicles] = useState([])
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    loadData()
  }, [userId])

  const loadData = async () => {
    try {
      setLoading(true)
      setError('')

      // Get public profile
      const profileRes = await getPublicProfile(userId)
      if (profileRes.error || !profileRes.data) {
        setError('This garage is not publicly available')
        setLoading(false)
        return
      }

      setProfile(profileRes.data)

      // Get public vehicles
      const vehiclesRes = await getPublicGarageVehicles(userId)
      if (vehiclesRes.error) throw vehiclesRes.error

      setVehicles(vehiclesRes.data || [])
    } catch (err) {
      console.error('Error loading public garage:', err)
      setError('Failed to load garage information')
    } finally {
      setLoading(false)
    }
  }

  const handleVehicleClick = async (vehicle) => {
    try {
      const res = await getPublicVehicleDetails(vehicle.id, userId)
      if (res.error) throw res.error
      setSelectedVehicle(res.data)
    } catch (err) {
      console.error('Error loading vehicle details:', err)
    }
  }

  // Calculate unique marques
  const uniqueMarques = [...new Set(vehicles.map(v => v.make))].length

  if (loading) {
    return (
      <div className="min-h-screen bg-rdc-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-rdc-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-rdc-taupe">Loading garage...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-rdc-cream flex items-center justify-center p-4">
        <div className="card max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={24} className="text-red-600" />
          </div>
          <h1 className="text-xl font-display font-semibold text-black mb-2">Garage Not Available</h1>
          <p className="text-rdc-taupe mb-6">{error}</p>
          <Link
            to="/welcome"
            className="btn-primary inline-block"
          >
            Create Your Own Garage
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-rdc-cream flex flex-col">
      {/* Navigation bar with branding */}
      <div className="bg-white border-b border-rdc-cream sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Car size={24} className="text-rdc-primary" />
              <span className="font-display font-bold text-lg text-black">Digital Garage</span>
            </div>
            <Link
              to="/welcome"
              className="flex items-center gap-2 text-rdc-primary hover:text-rdc-primary-dark transition-colors text-sm font-medium"
            >
              <span>Create Your Own</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-grow">
        {/* Profile Header */}
        <div className="bg-white border-b border-rdc-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-start justify-between gap-6 mb-8">
              <div className="flex-grow">
                <h1 className="text-5xl font-display font-bold text-black mb-2">
                  {profile?.full_name}'s Collection
                </h1>
                {profile?.member_since && (
                  <div className="flex items-center gap-2 text-rdc-taupe mt-3">
                    <Calendar size={16} />
                    <span className="text-sm">
                      Member since {format(new Date(profile.member_since), 'MMMM yyyy')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Collection Stats Bar */}
            {vehicles.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t border-rdc-cream">
                <div className="flex flex-col">
                  <div className="text-4xl font-bold text-rdc-primary mb-1">{vehicles.length}</div>
                  <div className="text-sm text-rdc-taupe">
                    {vehicles.length === 1 ? 'Vehicle' : 'Vehicles'}
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="text-4xl font-bold text-rdc-primary mb-1">{uniqueMarques}</div>
                  <div className="text-sm text-rdc-taupe">
                    {uniqueMarques === 1 ? 'Marque' : 'Marques'}
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="inline-flex items-center gap-2 mb-1">
                    <Shield size={20} className="text-rdc-primary" />
                    <span className="text-lg font-semibold text-black">Documented</span>
                  </div>
                  <div className="text-sm text-rdc-taupe">RDC Digital Garage</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main content area */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {vehicles.length === 0 ? (
            <div className="text-center py-16">
              <Car size={56} className="text-rdc-warm-gray mx-auto mb-6 opacity-40" />
              <h2 className="text-2xl font-display font-semibold text-black mb-3">No vehicles yet</h2>
              <p className="text-rdc-taupe mb-8 max-w-md mx-auto">
                This garage is empty or no vehicles are currently shared.
              </p>
              <Link
                to="/welcome"
                className="btn-primary inline-flex items-center gap-2"
              >
                Create Your Own Garage
                <ArrowRight size={18} />
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-display font-semibold text-black mb-2">
                  Vehicle Collection
                </h2>
                <p className="text-rdc-taupe">Click on any vehicle to view photos and specifications</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vehicles.map((vehicle) => (
                  <VehicleCard
                    key={vehicle.id}
                    vehicle={vehicle}
                    onClick={() => handleVehicleClick(vehicle)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer with privacy & CTA */}
      <div className="bg-white border-t border-rdc-cream mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Privacy notice */}
            <div className="bg-rdc-cream/30 rounded-lg p-6 border border-rdc-cream">
              <h3 className="font-display font-semibold text-black mb-3 flex items-center gap-2">
                <Shield size={18} className="text-rdc-primary" />
                Privacy First
              </h3>
              <p className="text-sm text-rdc-dark-gray leading-relaxed">
                This shared garage shows photos, specifications, and documentation status. Valuations, financial records, and personal details are never shared.
              </p>
            </div>

            {/* CTA */}
            <div className="bg-rdc-primary/5 rounded-lg p-6 border border-rdc-primary/10 flex flex-col justify-between">
              <div>
                <h3 className="font-display font-semibold text-black mb-2">
                  Start Your Own Digital Garage
                </h3>
                <p className="text-sm text-rdc-dark-gray mb-4">
                  Document your collection with photos, specs, and maintenance records.
                </p>
              </div>
              <Link
                to="/welcome"
                className="btn-primary self-start inline-flex items-center gap-2"
              >
                Get Started
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Powered by branding */}
          <div className="text-center pt-6 border-t border-rdc-cream">
            <p className="text-sm text-rdc-taupe">
              Powered by{' '}
              <span className="font-semibold text-rdc-primary">RDC Digital Garage</span>
            </p>
          </div>
        </div>
      </div>

      {/* Vehicle detail modal */}
      {selectedVehicle && (
        <VehicleDetailModal
          vehicle={selectedVehicle}
          onClose={() => setSelectedVehicle(null)}
        />
      )}
    </div>
  )
}
